<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { setColorTheme } from 'dashboard/helper/themeHelper';

import FormSelect from 'v3/components/Form/Select.vue';

defineProps({
  label: { type: String, default: '' },
  description: { type: String, default: '' },
});

const { t } = useI18n();
const { updateUISettings, uiSettings } = useUISettings();

const i18nKeyPrefix = 'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.APPEARANCE';

const currentScheme = computed(() => uiSettings.value?.color_scheme || 'auto');

const themeOptions = computed(() => [
  { name: t(`${i18nKeyPrefix}.OPTIONS.LIGHT`), value: 'light' },
  { name: t(`${i18nKeyPrefix}.OPTIONS.DARK`), value: 'dark' },
  { name: t(`${i18nKeyPrefix}.OPTIONS.AUTO`), value: 'auto' },
]);

const updateScheme = async scheme => {
  try {
    // eslint-disable-next-line no-console
    console.log('[THEME DEBUG] ThemeSelect updateScheme called with scheme =', scheme);
    await updateUISettings({ color_scheme: scheme });
    // eslint-disable-next-line no-console
    console.log('[THEME DEBUG] ThemeSelect after updateUISettings, uiSettings.value.color_scheme =', uiSettings.value?.color_scheme);
    setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches, scheme);
    useAlert(t(`${i18nKeyPrefix}.UPDATE_SUCCESS`));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('[THEME DEBUG] ThemeSelect updateScheme caught error =', error);
    useAlert(t(`${i18nKeyPrefix}.UPDATE_ERROR`));
  }
};

const selectedValue = computed({
  get: () => currentScheme.value,
  set: value => {
    updateScheme(value);
  },
});
</script>

<template>
  <div class="flex gap-2 justify-between w-full items-start">
    <div>
      <label class="text-n-gray-12 font-medium leading-6 text-sm">
        {{ label }}
      </label>
      <p class="text-n-gray-11">
        {{ description }}
      </p>
    </div>
    <FormSelect
      v-model="selectedValue"
      name="color_scheme"
      spacing="compact"
      class="min-w-28 mt-px"
      :options="themeOptions"
      label=""
    >
      <option
        v-for="option in themeOptions"
        :key="option.value"
        :value="option.value"
        :selected="option.value === selectedValue"
      >
        {{ option.name }}
      </option>
    </FormSelect>
  </div>
</template>
